/**
 * The Human Resources Overview (WALLIYO-0134), built to the approved design.
 *
 * Three tabs, because HR is three questions. What is waiting on you is a queue;
 * the shape of the company is a state; the weeks ahead are a calendar. One page
 * cannot lead with all three and none of them is worth burying — so each is a
 * tab, and **each tab carries its own count**, which is what makes the strip
 * worth having: it says which tab wants you before anybody opens one.
 *
 * The tab is in the hash (`#peopleOverview/weeks`), so a view is a link somebody
 * can send and a reload comes back where it was.
 */

import type { CompanyShape, PeopleOverview as Data, PeopleTask, WeeksAhead } from "@wallyo/contracts/console/overview";

import { t, tCount, type MessageId } from "../../i18n";
import { formatDate, formatNumber } from "../../i18n/format";

/** Which tab is open, from the hash. Unknown or absent means the first. */
export function peopleTabOf(hash: string, tabs: Data["tabs"]): string {
  const asked = hash.replace(/^#\/?/, "").split("/")[1] ?? "";
  return tabs.some((tab) => tab.tabId === asked) ? asked : (tabs[0]?.tabId ?? "waiting");
}

export function PeopleOverviewView({ data, tab }: { data: Data; tab: string }) {
  return (
    <>
      <div className="ov-tabs" role="tablist">
        {data.tabs.map((each) => {
          const on = each.tabId === tab;
          return (
            <a
              key={each.tabId}
              className={on ? "ov-tab on" : "ov-tab"}
              href={`#peopleOverview/${each.tabId}`}
              role="tab"
              aria-selected={on}
            >
              {t(`overview.people.tab.${each.tabId}` as MessageId)}
              {/* The count is the point of the strip: it says which tab wants
                  you without anybody opening it. Zero says nothing. */}
              {each.count > 0 ? <span className={`nav-pill ${each.tone}`}>{formatNumber(each.count)}</span> : null}
            </a>
          );
        })}
      </div>

      {tab === "company" && data.company ? (
        <Company company={data.company} />
      ) : tab === "weeks" && data.weeks ? (
        <Weeks weeks={data.weeks} />
      ) : (
        <Waiting data={data} />
      )}
    </>
  );
}

// ── Waiting on you ───────────────────────────────────────────────────────

function Waiting({ data }: { data: Data }) {
  return (
    <div className="ov-split">
      <section className="card ov-tasks">
        {data.waiting.length === 0 ? (
          <p className="foot">{t("overview.people.allClear")}</p>
        ) : (
          data.waiting.map((task, index) => <Task key={`${task.taskId}-${index}`} task={task} />)
        )}
      </section>

      <div className="ov-side">
        <section className="card">
          <h2>{t("overview.people.yours")}</h2>
          <div className="ov-line">
            <span className="grow">{t("overview.people.leaveLeft")}</span>
            <span className="mono ok-text">
              {data.yours.leaveDaysLeft === null
                ? t("overview.people.noAllowance")
                : tCount("overview.people.days", data.yours.leaveDaysLeft, {
                    days: formatNumber(data.yours.leaveDaysLeft),
                  })}
            </span>
          </div>
          <div className="ov-line">
            <span className="grow">{t("overview.people.coursesDue")}</span>
            <span className={data.yours.coursesDue > 0 ? "mono warn-text" : "mono"}>
              {formatNumber(data.yours.coursesDue)}
            </span>
          </div>
          <a className="go-link" href="#myTimeOff">
            {t("overview.people.askForTimeOff")}
          </a>
        </section>
      </div>
    </div>
  );
}

/**
 * One thing waiting on the reader.
 *
 * The circle carries initials when it is about one person and the count when it
 * is about several — a face for Fatoumata's leave, a number for seven grants —
 * and the second line says why it is urgent rather than repeating the first.
 */
function Task({ task }: { task: PeopleTask }) {
  return (
    <div className="ov-row">
      <span className={`ov-chip ${task.chipTone}`} aria-hidden="true">
        {task.chip}
      </span>
      <span className="grow">
        <span className="ov-row-what">
          {t(`overview.people.task.${task.taskId}` as MessageId, {
            subject: task.subject ?? "",
            count: formatNumber(task.count),
            // The weekday, in the reader's language: "starts on Monday".
            on: task.on ? formatDate(task.on, { weekday: "long" }) : "",
          })}
        </span>
        <span
          className={
            task.tone === "bad" ? "ov-row-why danger" : task.tone === "warn" ? "ov-row-why warn-text" : "ov-row-why"
          }
        >
          {/* The name comes first and the sentence after it, so "Support · no
              role assigned yet" reads the same way in every language without a
              separator baked into any of them. */}
          {task.detail ? `${task.detail} · ` : ""}
          {tCount(`overview.people.detail.${task.taskId}` as MessageId, task.waitingDays ?? 0, {
            days: formatNumber(task.waitingDays ?? 0),
            second: formatNumber(task.second),
            on: task.on ? formatDate(task.on, { day: "numeric", month: "long" }) : "",
          })}
        </span>
      </span>
      <a className={task.tone === "bad" || task.tone === "warn" ? "btn primary" : "btn"} href={task.href}>
        {t(`overview.action.${task.actionId}` as MessageId)}
      </a>
    </div>
  );
}

// ── The company ──────────────────────────────────────────────────────────

/**
 * The org, as a state: every team on one line with its lead and its size.
 *
 * A team running without a lead is what this tab exists to make visible, so a
 * missing lead is said in words rather than left as an empty cell.
 */
function Company({ company }: { company: CompanyShape }) {
  const biggest = Math.max(1, ...company.teams.map((team) => team.size));
  return (
    <>
      <div className="ov-figures">
        <div className="ov-figure">
          <span className="figure">{formatNumber(company.employees)}</span>
          <span className="foot">{t("overview.people.employees")}</span>
          {company.joined > 0 ? (
            <span className="foot ok-text ov-lost">
              ↑ {tCount("overview.people.joinedThisMonth", company.joined, { count: formatNumber(company.joined) })}
            </span>
          ) : null}
        </div>
        <div className="ov-figure">
          <span className="figure">{formatNumber(company.teams.length)}</span>
          <span className="foot">{t("overview.people.teams")}</span>
        </div>
        <div className="ov-figure">
          <span className="figure">{formatNumber(company.openRoles)}</span>
          <span className="foot">{t("overview.figure.openRoles")}</span>
          <span className="foot ov-lost">
            {tCount("overview.people.candidates", company.candidates, { count: formatNumber(company.candidates) })}
          </span>
        </div>
        {company.medianMonths === null ? null : (
          <div className="ov-figure">
            <span className="figure">{formatNumber(Math.round((company.medianMonths / 12) * 10) / 10)}</span>
            <span className="foot">{t("overview.people.medianStay")}</span>
          </div>
        )}
      </div>

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.people.whereEverybodyIs")}</h2>
            <span className="foot">{t("overview.people.oneLinePerTeam")}</span>
            <a className="go-link mkt-see-all" href="#teams">
              {t("screen.teams")}
            </a>
          </div>
          {company.teams.length === 0 ? (
            <p className="foot">{t("overview.people.noTeamsYet")}</p>
          ) : (
            company.teams.map((team) => (
              <div key={team.teamId} className="ov-line ov-team">
                <span className="ov-team-name">{team.name}</span>
                <span className="ov-bar ov-team-bar" role="img" aria-label={formatNumber(team.size)}>
                  <span className="ov-bar-done" style={{ inlineSize: `${(team.size / biggest) * 100}%` }} />
                </span>
                <span className="mono ov-team-size">{formatNumber(team.size)}</span>
                <span className={team.lead === null ? "foot warn-text ov-team-lead" : "foot ov-team-lead"}>
                  {team.lead ?? t("overview.people.noLead")}
                </span>
              </div>
            ))
          )}
        </section>

        <div className="ov-side">{company.payroll ? <Payroll payroll={company.payroll} /> : null}</div>
      </div>
    </>
  );
}

/** Where payroll has got to, and how long is left to finish it. */
function Payroll({ payroll }: { payroll: NonNullable<CompanyShape["payroll"]> }) {
  const done = payroll.linesTotal === 0 ? 0 : Math.round((payroll.linesChecked / payroll.linesTotal) * 100);
  // "September is open", not "this month is open": the month has a name and the
  // design uses it, capitalised because it opens the sentence.
  const named = formatDate(payroll.month, { month: "long" });
  const month = named.charAt(0).toLocaleUpperCase() + named.slice(1);
  return (
    <section className="card">
      <h2>{t("overview.people.payroll")}</h2>
      <p className="foot">
        {payroll.open
          ? payroll.closesInDays <= 0
            ? t("overview.people.payrollDue", { month })
            : tCount("overview.people.payrollOpen", payroll.closesInDays, {
                month,
                days: formatNumber(payroll.closesInDays),
              })
          : t("overview.people.payrollClosed", { month })}
      </p>
      {/* A bar, because "26 of 34" is a fraction somebody has to do in their
          head and this is the same fact at a glance. */}
      <div className="ov-bar" role="img" aria-label={`${done}%`}>
        <div className="ov-bar-done" style={{ inlineSize: `${done}%` }} />
      </div>
      <div className="foot ov-bar-say">
        {t("overview.people.linesChecked", {
          checked: formatNumber(payroll.linesChecked),
          total: formatNumber(payroll.linesTotal),
        })}
      </div>
    </section>
  );
}

// ── The weeks ahead ──────────────────────────────────────────────────────

/**
 * Four weeks across, people down, and what lands in each.
 *
 * The only view here that can show two people away in the same week, which is
 * the failure a list cannot catch: each request is reasonable on its own, and
 * together they leave a team with nobody.
 */
function Weeks({ weeks }: { weeks: WeeksAhead }) {
  const markOf = (week: string) => weeks.marks.filter((mark) => mark.week === week);
  return (
    <>
      {weeks.thin.length > 0 ? (
        <section className="card ov-callout bad">
          <h2>{t("overview.people.nobodyCovering")}</h2>
          {weeks.thin.map((gap) => (
            <p key={`${gap.team}-${gap.week}`} className="foot danger">
              {t("overview.people.thinWeek", {
                team: gap.team,
                week: formatDate(gap.week, { day: "numeric", month: "long" }),
                away: formatNumber(gap.away),
                size: formatNumber(gap.size),
              })}
            </p>
          ))}
        </section>
      ) : null}

      <section className="card">
        <div className="ov-card-head">
          <h2>{t("overview.people.whoIsWhere")}</h2>
          <span className="foot">{t("overview.people.fourWeeks")}</span>
          <a className="go-link mkt-see-all" href="#hr/employees/time-off">
            {t("overview.people.allTimeOff")}
          </a>
        </div>

        {weeks.rows.length === 0 ? (
          <p className="foot">{t("overview.people.quietWeeks")}</p>
        ) : (
          <div
            className="ov-weeks"
            style={{ gridTemplateColumns: `140px repeat(${weeks.weeks.length}, minmax(0, 1fr))` }}
          >
            <div />
            {weeks.weeks.map((week) => (
              <div key={week} className="ov-week-head">
                {formatDate(week, { day: "numeric", month: "short" })}
                {markOf(week).map((mark) => (
                  <span key={mark.markId} className="ov-week-mark">
                    {t(`overview.people.mark.${mark.markId}` as MessageId, {
                      on: formatDate(mark.on, { weekday: "short" }),
                    })}
                  </span>
                ))}
              </div>
            ))}

            {weeks.rows.map((row) => (
              <div key={row.staffMemberId} className="ov-week-row">
                <div className="ov-week-who">{row.name}</div>
                {weeks.weeks.map((week) => {
                  const span = row.spans.find((each) => each.week === week);
                  return (
                    <div key={week} className="ov-week-cell">
                      {span ? (
                        <span className={`ov-span ${span.kindId}`}>
                          {t(`overview.people.span.${span.kindId}` as MessageId, {
                            days: formatNumber(span.days),
                          })}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
